(() => {
  const ADD_SOURCES = new Set(['product-form', 'quick-add', 'quick-add-bulk', 'quick-order-list']);
  let cartEcommerceCache = null;

  function whenReady(callback) {
    if (window.GA4?.ready) {
      callback();
      return;
    }
    document.addEventListener('ga4:ready', callback, { once: true });
  }

  function stripListFields(item) {
    const result = { ...item };
    delete result.item_list_name;
    delete result.item_list_id;
    delete result.index;
    return result;
  }

  function itemForListEvent(item) {
    return { ...item, quantity: '1' };
  }

  function itemForCartEvent(item) {
    return stripListFields(item);
  }

  function getVariantMap() {
    const el = document.querySelector('[data-ga4-variants-json]');
    if (!el) return {};

    try {
      const variants = JSON.parse(el.textContent);
      return Object.fromEntries(variants.map((variant) => [String(variant.item_id), variant]));
    } catch (error) {
      console.warn('GA4: invalid variants JSON', error);
      return {};
    }
  }

  function applyVariant(item, variantId) {
    const variant = getVariantMap()[String(variantId)];
    if (!variant || !item) return item;

    return window.GA4.normalizeItem({
      ...item,
      item_id: variant.item_id,
      id: variant.item_id,
      price_cents: variant.price_cents,
      item_variant: variant.item_variant,
    });
  }

  function findItemByVariantId(variantId) {
    let match = null;
    document.querySelectorAll('[data-ga4-item]').forEach((element) => {
      const item = window.GA4.parseItemFromElement(element);
      if (item?.item_id === String(variantId)) match = item;
    });
    return match;
  }

  function itemFromCartLine(line) {
    const variantId = String(line.variant_id || line.id || '');
    const row = document.querySelector(`[data-ga4-variant-id="${variantId}"]`);
    const parsed = row ? window.GA4.parseItemFromElement(row) : null;

    if (parsed) {
      return window.GA4.normalizeItem({
        ...parsed,
        quantity: String(line.quantity),
      });
    }

    return window.GA4.normalizeItem({
      item_name: line.product_title || line.title,
      item_id: line.variant_id || line.id,
      price_cents: line.final_price || line.price,
      item_brand: line.vendor || '',
      item_category: line.product_type || '',
      item_category2: '',
      item_variant: line.variant_title === 'Default Title' ? '' : line.variant_title || '',
      quantity: String(line.quantity),
    });
  }

  function ecommerceFromCart(cart) {
    if (!cart?.items?.length) return { value: '0.00', items: [] };

    const items = cart.items.map((line) => itemForCartEvent(itemFromCartLine(line)));
    const value = items
      .reduce((total, item) => total + Number(item.price) * Number(item.quantity), 0)
      .toFixed(2);

    return { value, items };
  }

  function setCartCache(cart) {
    cartEcommerceCache = ecommerceFromCart(cart);
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    if (!response.ok) throw new Error('GA4: cart.js request failed');
    return response.json();
  }

  async function buildCartEcommerce() {
    return ecommerceFromCart(await fetchCart());
  }

  function pushBeginCheckout(ecommerce) {
    if (!ecommerce?.items?.length || !window.GA4?.ready) return;
    window.GA4.pushEvent('begin_checkout', ecommerce);
  }

  const trackedViewItemLists = new Set();

  function pushViewItemList(listId, listName, items) {
    if (!items.length) return;

    const key = `${listId}::${listName}`;
    if (trackedViewItemLists.has(key)) return;
    trackedViewItemLists.add(key);

    window.GA4.pushEvent('view_item_list', {
      value: window.GA4.sumItemPrices(items),
      items,
    });
  }

  function buildListItems(rawItems, listName, listId) {
    return rawItems.map((item, index) =>
      itemForListEvent({
        ...item,
        item_list_name: item.item_list_name || listName,
        item_list_id: item.item_list_id || listId,
        index: item.index || index + 1,
      })
    );
  }

  function trackViewItemLists(root) {
    const scope = root?.querySelectorAll ? root : document;
    const listContainers = scope.querySelectorAll('[data-ga4-item-list]');

    if (listContainers.length) {
      listContainers.forEach((container) => {
        const listName = container.dataset.ga4ListName || '';
        const listId = container.dataset.ga4ListId || '';
        const rawItems = window.GA4.itemsFromElements(container.querySelectorAll('[data-ga4-item]'));
        const items = buildListItems(rawItems, listName, listId);

        pushViewItemList(listId || items[0]?.item_list_id, listName || items[0]?.item_list_name, items);
      });
      return;
    }

    const groups = new Map();

    scope.querySelectorAll('[data-ga4-item]').forEach((element) => {
      const item = window.GA4.parseItemFromElement(element);
      if (!item) return;

      const key = `${item.item_list_id}::${item.item_list_name}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(itemForListEvent(item));
    });

    groups.forEach((items) => {
      pushViewItemList(items[0]?.item_list_id, items[0]?.item_list_name, items);
    });
  }

  function trackViewItem() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const item = window.GA4.parseItemFromElement(productInfo);
    if (!item) return;

    const eventItem = itemForCartEvent({ ...item, quantity: '1' });
    window.GA4.pushEvent('view_item', {
      value: eventItem.price,
      items: [eventItem],
    });
  }

  async function trackViewCart() {
    const ecommerce = await buildCartEcommerce();
    if (!ecommerce.items.length) return;
    cartEcommerceCache = ecommerce;
    window.GA4.pushEvent('view_cart', ecommerce);
  }

  function getAddToCartQuantity(source) {
    if (source !== 'product-form') return 1;
    const quantityInput = document.querySelector('product-form [name="quantity"]');
    return parseInt(quantityInput?.value, 10) || 1;
  }

  function initAddToCart() {
    if (typeof subscribe !== 'function') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!ADD_SOURCES.has(event.source)) return;

      whenReady(() => {
        const variantId = String(event.productVariantId || '');
        if (!variantId) return;

        let item = null;
        const productInfo = document.querySelector('product-info');
        if (productInfo) {
          item = applyVariant(window.GA4.parseItemFromElement(productInfo), variantId);
        }

        if (!item) item = findItemByVariantId(variantId);

        const cartLine = event.cartData?.items?.find((line) => String(line.variant_id || line.id) === variantId);
        if (!item && cartLine) item = itemFromCartLine(cartLine);

        if (!item) return;

        const quantity = getAddToCartQuantity(event.source);
        const eventItem = itemForCartEvent({ ...item, quantity: String(quantity) });
        const value = (Number(eventItem.price) * quantity).toFixed(2);

        window.GA4.pushEvent('add_to_cart', {
          value,
          items: [eventItem],
        });
      });
    });
  }

  function initVariantTracking() {
    if (typeof subscribe !== 'function') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variantId = event.data?.variant?.id;
      if (!variantId) return;

      whenReady(() => {
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        let item = window.GA4.parseItemFromElement(productInfo);
        if (!item) return;

        item = applyVariant(item, variantId);
        const eventItem = itemForCartEvent({ ...item, quantity: '1' });

        window.GA4.pushEvent('view_item', {
          value: eventItem.price,
          items: [eventItem],
        });
      });
    });
  }

  function initCartCache() {
    if (typeof subscribe !== 'function') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event.cartData?.items) return;
      whenReady(() => setCartCache(event.cartData));
    });
  }

  function submitCheckoutForm(checkoutBtn) {
    const form =
      checkoutBtn.form || document.getElementById(checkoutBtn.getAttribute('form') || '');

    if (!form) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(checkoutBtn);
      return;
    }

    form.submit();
  }

  function initCartTracking() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const originalOpen = drawer.open.bind(drawer);
      drawer.open = (...args) => {
        originalOpen(...args);
        whenReady(() => {
          trackViewCart().catch((error) => console.warn('GA4: view_cart failed', error));
        });
      };
    }

    if (window.location.pathname.includes('/cart')) {
      whenReady(() => {
        trackViewCart().catch((error) => console.warn('GA4: view_cart failed', error));
      });
    }

    document.addEventListener(
      'click',
      (event) => {
        const checkoutBtn = event.target.closest('[name="checkout"]');
        if (!checkoutBtn || !window.GA4?.ready) return;

        if (cartEcommerceCache?.items?.length) {
          pushBeginCheckout(cartEcommerceCache);
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        buildCartEcommerce()
          .then((ecommerce) => {
            cartEcommerceCache = ecommerce;
            pushBeginCheckout(ecommerce);
            submitCheckoutForm(checkoutBtn);
          })
          .catch((error) => {
            console.warn('GA4: begin_checkout failed', error);
            submitCheckoutForm(checkoutBtn);
          });
      },
      true
    );
  }

  function initPageEvents() {
    whenReady(() => {
      trackViewItem();
      trackViewItemLists();

      if (!trackedViewItemLists.size) {
        window.addEventListener(
          'load',
          () => {
            whenReady(() => trackViewItemLists());
          },
          { once: true }
        );
      }
    });
  }

  function init() {
    initPageEvents();
    initVariantTracking();
    initAddToCart();
    initCartCache();
    initCartTracking();

    document.addEventListener('collection:grid:updated', () => {
      whenReady(() => trackViewItemLists());
    });

    document.addEventListener('collection:grid:appended', () => {
      whenReady(() => trackViewItemLists());
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
